/**
 * Trunnion AI - Collapsible Sidebar Navigation
 */

import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  GitBranch,
  Play,
  Bot,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LayoutTemplate,
  Target,
  Sliders,
} from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { ROUTES } from '@/config/routes';

const mainNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.DASHBOARD },
  { icon: GitBranch, label: 'Workflows', path: ROUTES.WORKFLOWS },
  { icon: Play, label: 'Executions', path: ROUTES.EXECUTIONS },
  { icon: Bot, label: 'Agents', path: ROUTES.AGENTS },
  { icon: LayoutTemplate, label: 'Templates', path: ROUTES.TEMPLATES },
  { icon: Target, label: 'Missions', path: ROUTES.MISSIONS },
  { icon: FileText, label: 'Audit Log', path: ROUTES.AUDIT },
  { icon: Sliders, label: 'Admin', path: ROUTES.ADMIN },
];

const bottomNavItems = [
  { icon: Settings, label: 'Settings', path: ROUTES.SETTINGS },
];

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  return (
    <aside
      className={`flex flex-col border-r border-trunnion-slate-700 bg-trunnion-slate-900 transition-all duration-200 ${
        sidebarCollapsed ? 'w-16' : 'w-60'
      }`}
      aria-label="Main navigation"
    >
      <nav className="flex-1 px-2 py-3 space-y-1">
        {mainNavItems.map(({ icon: Icon, label, path }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-trunnion-gold-500/10 text-trunnion-gold-500'
                  : 'text-trunnion-slate-300 hover:bg-trunnion-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`
            }
            title={sidebarCollapsed ? label : undefined}
            end={path === ROUTES.ADMIN}
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!sidebarCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom pinned items */}
      <div className="border-t border-trunnion-slate-700 px-2 py-2 space-y-1">
        {bottomNavItems.map(({ icon: Icon, label, path }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-trunnion-gold-500/10 text-trunnion-gold-500'
                  : 'text-trunnion-slate-300 hover:bg-trunnion-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`
            }
            title={sidebarCollapsed ? label : undefined}
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!sidebarCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
        <button
          onClick={toggleSidebar}
          className="flex w-full items-center justify-center rounded-lg p-2 text-trunnion-slate-400 hover:bg-trunnion-slate-800 hover:text-white transition-colors"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
